import { useState, useCallback, SetStateAction, Dispatch } from 'react';
import { useSelector } from 'react-redux';
import { IAllStates } from '../../store/rootReducer';
import { DOWNLOAD_INVOICE, INFLUENCER_INVOICE } from '../../utils/endpoints';
import { downloadFileAPI, multipartFormData } from '../../utils';
import { ErrorToast, SuccessToast } from '../../utils/toasters';
import { InfluencerInvoiceService } from '../../app/influencer/invoices/service';
import { Campaign } from '../../model/campaign/Campaign';

export interface UploadModalState {
    show: boolean;
    sequenceNumber?: number;
    invoiceId?: number;
    rejectReason?: string;
    invoiceFile?: string;
    invoiceLength?: number;
    campaignTitle?: string;
    isLongTermOffer?: boolean;
    selectedCampaignId?: number;
}

export interface UseInvoicesProps {
    t: any;
    filterParams: any;
    setFilterParams: (value: unknown) => void;
    selectedCampaign?: Campaign | Campaign[] | undefined;
    setSelectedCampaign?: Dispatch<SetStateAction<Campaign | Campaign[] | undefined>> | Dispatch<SetStateAction<Campaign | undefined>>;
    isArchivedInvoice?: boolean;
}

export const useInvoices = ({
    t,
    filterParams,
    setFilterParams,
    setSelectedCampaign,
    isArchivedInvoice,
}: UseInvoicesProps) => {
    const [uploadModalData, setUploadModalData] = useState<UploadModalState>({ show: false });
    const [showDeleteModal, setShowDeleteModal] = useState({ show: false, id: 0, title: '' });
    const [isUploading, setIsUploading] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);

    const { user } = useSelector((state: IAllStates) => state.auth);
    const { selectedInfluencer } = useSelector((state: IAllStates) => state.agency);

    const buildUploadUrl = useCallback(() => {
        const isReplace = !!uploadModalData.invoiceFile;

        if (isReplace) {
            return {
                url: `${INFLUENCER_INVOICE}/replace/${uploadModalData.invoiceId}`,
                method: 'put',
            };
        }
        const base = `${INFLUENCER_INVOICE}/${uploadModalData?.selectedCampaignId}/${
            selectedInfluencer?.id || user.id
        }`;
        const params = `?isLongTerm=${uploadModalData?.isLongTermOffer ?? false}`;
        const position = uploadModalData.sequenceNumber ? `&position=${uploadModalData.sequenceNumber}` : '';

        return {
            url: `${base}${params}${position}`,
            method: 'post',
        };
    }, [uploadModalData, user.id, selectedInfluencer?.id]);

    const handleSubmitUpload = useCallback(
        async (files?: Blob[] | MediaSource[]) => {
            const { url, method } = buildUploadUrl();
            setIsUploading(true);

            try {
                const response = await multipartFormData(files, url, null, method);

                if (method === 'put') {
                    setUploadModalData({ show: false });
                    setFilterParams((prev: any) => ({ ...prev, updatedAt: Date.now() }));
                } else {
                    setFilterParams((prev: any) => ({ ...prev, perPage: 12 }));
                }

                // Update only the modified campaign in the array
                if (isArchivedInvoice) {
                    if (setSelectedCampaign) {
                        (setSelectedCampaign as Dispatch<SetStateAction<Campaign | Campaign[] | undefined>>)((prev: Campaign | Campaign[] | undefined) => {
                            if (!Array.isArray(prev)) return prev;
                            return prev.map(c => (c.id === response?.data?.id ? response.data : c));
                        });
                    }
                } else {
                    if (setSelectedCampaign) {
                        setSelectedCampaign(response?.data ?? undefined);
                    }
                }
                SuccessToast(t('general.toasts.success_upload_file'));
            } catch (error) {
                ErrorToast(error);
            } finally {
                setIsUploading(false);
            }
        },
        [buildUploadUrl, filterParams, setFilterParams, t, setSelectedCampaign]
    );

    const deleteInvoice = useCallback(async () => {
        setIsDeleting(true);
        try {
            const response = await InfluencerInvoiceService.deleteInvoice(showDeleteModal.id);
            const updated = response?.data ?? response;

            SuccessToast(t('influencer.invoice.toasts.success_deleted_invoice'));
            setFilterParams({ ...filterParams, perPage: 12, updatedAt: Date.now() });
            if (isArchivedInvoice) {
                if (setSelectedCampaign) {
                    (setSelectedCampaign as Dispatch<SetStateAction<Campaign | Campaign[] | undefined>>)((prev: Campaign | Campaign[] | undefined) => {
                        if (!Array.isArray(prev)) return prev;
                        return prev.map(c => (c.id === response?.data?.id ? response.data : c));
                    });
                }
            } else {
                if (setSelectedCampaign) {
                    setSelectedCampaign(updated ?? undefined);
                }
            }
        } catch (error) {
            ErrorToast(error);
        } finally {
            setIsDeleting(false);
        }
    }, [showDeleteModal, filterParams, setFilterParams, t, setSelectedCampaign]);

    const downloadInvoice = useCallback(async (id: number) => {
        setIsDownloading(true);
        try {
            await downloadFileAPI(`${DOWNLOAD_INVOICE}/${id}`);
        } catch (error) {
            ErrorToast(error);
        } finally {
            setIsDownloading(false);
        }
    }, []);

    return {
        uploadModalData,
        setUploadModalData,
        showDeleteModal,
        setShowDeleteModal,
        handleSubmitUpload,
        deleteInvoice,
        downloadInvoice,
        isUploading,
        isDeleting,
        isDownloading,
    };
};
